import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { addRemoveError, checkBoxHelper } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { toNum } from '@shared/helpers/math.helper';
import { getCoopGaranziaFidiControl } from './helpers/coop-garanzia-fidi-form.helpers';
import { formPaths } from '@shared/constants/form-paths.const';

const TIPO_BANCA_ITALIA = "Banca d'Italia (ex 107 TUB)";
const TIPO_ORGANISMO_VIGILANZA = 'Organismo di Vigilanza (ex 106 TUB)';

export const schedaCoopGaranziaFidiRules: FormGroupRules = {
    my_CkCoopFidiConforme: (form: FormGroup) => {
        requiredByScCoopFidiObbl(form, 'my_CkCoopFidiConforme');
    },

    my_CkQuotaSocFidi: (form: FormGroup) => {
        requiredByScCoopFidiObbl(form, 'my_CkQuotaSocFidi');
    },

    my_CkCapSocPerFidi: (form: FormGroup) => {
        requiredByScCoopFidiObbl(form, 'my_CkCapSocPerFidi', 'Campo obbligatorio, verificare il Capitale Sociale nella scheda Bilancio');
    },

    my_CkPatrNettoPerFidi: (form: FormGroup) => {
        requiredByScCoopFidiObbl(form, 'my_CkPatrNettoPerFidi', 'Campo obbligatorio, verificare il Patrimonio Netto nella scheda Bilancio');
    },

    my_CkQuotaSocPercFidi: (form: FormGroup) => {
        requiredByScCoopFidiObbl(form, 'my_CkQuotaSocPercFidi');
    },

    my_CkVersFondiGarFidi: (form: FormGroup) => {
        requiredByScCoopFidiObbl(form, 'my_CkVersFondiGarFidi');
    },

    my_tipoVigilanzaFidi: (form: FormGroup) => {
        requiredByScCoopFidiObbl(form, 'my_tipoVigilanzaFidi');
    },

    my_ServiziAiSociFidi: (form: FormGroup) => {
        requiredByTipoVigilanza(form, 'my_ServiziAiSociFidi', TIPO_BANCA_ITALIA);
    },

    my_CkFidiSchemaBilancioConforme: (form: FormGroup) => {
        requiredByTipoVigilanza(form, 'my_CkFidiSchemaBilancioConforme', TIPO_BANCA_ITALIA);
    },

    my_CkFidiVolAttBanca: (form: FormGroup) => {
        requiredByTipoVigilanza(form, 'my_CkFidiVolAttBanca', TIPO_BANCA_ITALIA);
    },

    my_CkSoloGaranziaFidi: (form: FormGroup) => {
        requiredByTipoVigilanza(form, 'my_CkSoloGaranziaFidi', TIPO_ORGANISMO_VIGILANZA);
    },

    my_CkFidiSchemaBilVig: (form: FormGroup) => {
        requiredByTipoVigilanza(form, 'my_CkFidiSchemaBilVig', TIPO_ORGANISMO_VIGILANZA);
    },

    my_CkFidiCompetenza: (form: FormGroup) => {
        competenzaHelper(form);
    },

    my_CkFidiVolAttBancaDiscip: (form: FormGroup) => {
        const control = getCoopGaranziaFidiControl(form, 'my_CkFidiVolAttBancaDiscip');
        const tipoVigilanza: string = getCoopGaranziaFidiControl(form, 'my_tipoVigilanzaFidi').value;
        const ckFidiVolAttBanca: string = getCoopGaranziaFidiControl(form, 'my_CkFidiVolAttBanca').value;
        const scCoopFidiObbl: string = getCoopGaranziaFidiControl(form, 'my_ScCoopFidiObbl').value;
        const errors = control.errors ?? {};

        addRemoveError(
            isNilOrEmpty(control.value) && tipoVigilanza === TIPO_BANCA_ITALIA && ckFidiVolAttBanca === '0' && scCoopFidiObbl === '1',
            errors,
            'my_CkFidiVolAttBancaDiscip',
            { status: REQUIRED_STATUS }
        );

        control.setErrors(isEqual(errors, {}) ? null : errors);
    },
};

const requiredByScCoopFidiObbl = (form: FormGroup, targetPath: string, errorStatus?: string): void => {
    checkBoxHelper(form, targetPath, 'my_ScCoopFidiObbl', 1, formPaths.schedaCoopFidi, true, errorStatus);
};

const requiredByTipoVigilanza = (form: FormGroup, targetPath: string, expectedTipo: string): void => {
    const control = getCoopGaranziaFidiControl(form, targetPath);
    const tipoVigilanza: string = getCoopGaranziaFidiControl(form, 'my_tipoVigilanzaFidi').value;
    const scCoopFidiObbl: string = getCoopGaranziaFidiControl(form, 'my_ScCoopFidiObbl').value;
    const errors = control.errors ?? {};

    addRemoveError(isNilOrEmpty(control.value) && tipoVigilanza === expectedTipo && scCoopFidiObbl === '1', errors, targetPath, { status: REQUIRED_STATUS });

    control.setErrors(isEqual(errors, {}) ? null : errors);
};

const competenzaHelper = (form: FormGroup): void => {
    const checkValue = (v: string | boolean) => {
        return isNilOrEmpty(v) || toNum(v) !== 1;
    };
    const prControl = getCoopGaranziaFidiControl(form, 'my_CkFidiCompetenzaPr');
    const rgControl = getCoopGaranziaFidiControl(form, 'my_CkFidiCompetenzaRg');
    const nzControl = getCoopGaranziaFidiControl(form, 'my_CkFidiCompetenzaNz');
    const scCoopFidiObbl: string = getCoopGaranziaFidiControl(form, 'my_ScCoopFidiObbl').value;
    const prErrors = prControl.errors ?? {};
    const rgErrors = rgControl.errors ?? {};
    const nzErrors = nzControl.errors ?? {};

    const noneChecked = checkValue(prControl.value) && checkValue(rgControl.value) && checkValue(nzControl.value);
    const hasError = noneChecked && scCoopFidiObbl === '1';

    addRemoveError(hasError, prErrors, 'my_CkFidiCompetenzaPr', { status: 'Definire la competenza' });
    addRemoveError(hasError, rgErrors, 'my_CkFidiCompetenzaRg', { status: 'Definire la competenza' });
    addRemoveError(hasError, nzErrors, 'my_CkFidiCompetenzaNz', { status: 'Definire la competenza' });

    prControl.setErrors(isEqual(prErrors, {}) ? null : prErrors);
    rgControl.setErrors(isEqual(rgErrors, {}) ? null : rgErrors);
    nzControl.setErrors(isEqual(nzErrors, {}) ? null : nzErrors);
};

export const CoopGaranziaFidiFormValidators = Object.values(schedaCoopGaranziaFidiRules);
